/**
 * The craft-command hook (M8 plan MC-0b, review fix R1-B1): one registry and one `UiActions.craft?` port carry every
 * lane-K career command (berth, fabrication, study, Rush spares, data-core decode), so `career/runtime.ts` and
 * `ui/contracts.ts` are edited for them once.
 *
 * - **Commands.** A hook user adds its commands to `CraftCommandMap` by declaration merging, keyed by the command's
 *   `kind` (a dotted `<area>.<verb>` name), in its own module:
 *
 *   ```ts
 *   declare module '../../craft/hook' {
 *     interface CraftCommandMap {
 *       'berth.store': { kind: 'berth.store'; stationId: string; sel: BerthSelection }
 *     }
 *   }
 *   ```
 *
 *   `CraftCommand` is the union of the map's values; `UiActions.craft(cmd)` takes it.
 * - **Registration.** Each hook user registers its handlers from its own `src/features/<name>/feature.ts` (the
 *   auto-glob in `app/features.ts` loads it): `registerCraftHandler(kind, handler)` in the feature's `init`, keeping
 *   the returned function to unregister in `dispose`. A second registration of a kind throws.
 * - **Handlers** are `(cmd, ctx) => CmdOutcome`. They read the Career with `ctx.career()`, apply a pure Career →
 *   Career command and finish with `ctx.commit(next, effects)` + `ctx.done()`, or `ctx.refuse(error)`. Async work (a
 *   strategic buy, MC-c3b's buy-missing) runs inside `ctx.inFlight(work)`: the runtime's one-trade-in-flight lock,
 *   so it can't overlap a ware or goods trade, an undock or `boardShip`. Inside it, `ctx.market.commitGoods` sends
 *   the trade to the worker and books the executed fill (by default into the hold; a `book` routes it elsewhere,
 *   e.g. straight into a fab order's `delivered`).
 * - The runtime refuses a command when no career runs or no handler is registered for its kind, and turns a handler
 *   that throws into a refusal (logged).
 */
import type { Career, GoodOrWare } from '../career/types'
import type { CareerEffect, CmdResult, CommandError } from '../career/result'
import type { M2Data, StationDef } from '../data/m2Schemas'
import type { FabSystem } from './recipes'
import type { MarketQuote } from '../strategic-link/market'
import type { CmdOutcome } from '../ui/contracts'

/**
 * Every craft command by kind; empty here and extended by declaration merging in each hook user's module. Each
 * entry is `'<kind>': { kind: '<kind>'; ...arguments }`.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- extended by declaration merging
export interface CraftCommandMap {}

export type CraftCommandKind = keyof CraftCommandMap & string

/** Any registered craft command (the union of `CraftCommandMap`'s values). */
export type CraftCommand = CraftCommandMap[CraftCommandKind]

/** What the runtime reads of every craft command. */
export interface CraftCommandBase {
  readonly kind: string
}

/** An executed strategic trade: the units that moved, the executed unit price and the credits (rounded). */
export interface CraftFill {
  qty: number
  unit: number
  credits: number
}

/**
 * Books an executed trade on the Career (called with the current Career after the worker answered). A refusal here
 * means the trade executed but was not booked (it is logged); validate before committing.
 */
export type CraftBook = (career: Career, fill: CraftFill) => CmdResult<Career>

/** The strategic market as a craft handler sees it (null in `CraftCtx.market` without a strategic link). */
export interface CraftMarket {
  /**
   * The strategic quote for `qty` units of `item` at `stationId` (a career or strategic station id), with the
   * player's standing there, or null when the station doesn't trade it on `side` or the goods flag forbids it.
   */
  quote(stationId: string, item: GoodOrWare, qty: number, side: 'buy' | 'sell'): MarketQuote | null
  /**
   * Commits `qty` units of `item` at `stationId` through the worker at the local quote and books the fill: by default
   * a good into (`buy`) or out of (`sell`) the hold through `addGoods`/`removeGoods`; with `book`, however `book`
   * says. A raw ware needs `book`. Applies the goods flag, the M7 retaliation refusals and the price check like a
   * goods trade. Only valid inside `ctx.inFlight`; refused otherwise. Resolves with the refusal, or null once booked.
   */
  commitGoods(stationId: string, item: GoodOrWare, qty: number, side: 'buy' | 'sell', book?: CraftBook): Promise<CommandError | null>
}

/** The context a craft handler runs with (built by the career runtime for each command). */
export interface CraftCtx {
  /** The current Career, with the clock folded in. Read it again after an `await`. */
  career(): Career
  /** Replaces the Career (effects become notices). After the career ended (async work) it is logged and ignored. */
  commit(next: Career, effects?: readonly CareerEffect[]): void
  /** The docked station, or null in space. */
  readonly station: StationDef | null
  readonly data: M2Data
  /** The game time when the command ran. */
  readonly now: number
  /**
   * The docked system for Mk III geography (MC-c6, `offeredAt`): the strategic client's live view of the local system.
   * Absent without one (no strategic link, or no current view), which keeps the any-station rule.
   */
  readonly system?: FabSystem
  /** The strategic market, or null without a strategic link (M2's own market has no goods). */
  readonly market: CraftMarket | null
  /**
   * Runs `work` as the one trade in flight: refused at once (`notReady`) while another trade settles, else started;
   * its refusal later shows as `lastError`, its success republishes. Return this from the handler.
   */
  inFlight(work: () => Promise<CommandError | null>): CmdOutcome
  /** A refusal: shown inline until the next action. */
  refuse(error: CommandError): CmdOutcome
  /** Success: clears the inline error and republishes. */
  done(): CmdOutcome
}

/** A craft command's handler. `C` is its command (from `CraftCommandMap`). */
export type CraftHandler<C = CraftCommandBase> = (cmd: C, ctx: CraftCtx) => CmdOutcome

/** A handler registry (the game has one, `craftRegistry`; tests may make their own). */
export interface CraftRegistry {
  /** Registers `handler` for `kind`; throws when `kind` already has one. Returns the unregister function. */
  register(kind: string, handler: CraftHandler): () => void
  /** The handler for `kind`, or undefined. */
  handler(kind: string): CraftHandler | undefined
  /** The registered kinds, sorted. */
  kinds(): string[]
}

export function createCraftRegistry(): CraftRegistry {
  const handlers = new Map<string, CraftHandler>()
  return {
    register(kind: string, handler: CraftHandler): () => void {
      if (kind.length === 0) throw new Error('craft hook: a command kind must not be empty')
      if (handlers.has(kind)) throw new Error(`craft hook: "${kind}" is already registered`)
      handlers.set(kind, handler)
      return () => {
        if (handlers.get(kind) === handler) handlers.delete(kind)
      }
    },
    handler: (kind: string) => handlers.get(kind),
    kinds: () => [...handlers.keys()].sort(),
  }
}

/** The game's registry: the career runtime dispatches `UiActions.craft` through it. */
export const craftRegistry: CraftRegistry = createCraftRegistry()

/** Registers the game's handler for `kind` (from a feature's `init`); returns the unregister function for `dispose`. */
export function registerCraftHandler<K extends CraftCommandKind>(kind: K, handler: CraftHandler<CraftCommandMap[K]>): () => void {
  return craftRegistry.register(kind, handler as CraftHandler)
}
